import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { BASE_URL, useHubLogin } from '../../session';
import { authClientFor, hubFetch, targetSession } from '../../identity/session';
import type { Issue } from './shared';

// F3 "Migration to AUTH": per service (F2 `/admin/auth-usage`, summed over its pods by the hub), which callers still
// use their legacy credential and which already send AUTH tokens.

export type AuthMethod = 'jwt' | 'legacy' | 'none' | 'other';

export interface AuthUsageCaller {
  /** `tenant:<tid>` (JWT), `client:<id>`, `sub:<sub>`, or the AUTH alias of a legacy caller (`pms:1546`). */
  callerKey: string;
  method: AuthMethod;
  kind: 'user' | 'service' | 'legacy' | 'anonymous' | null;
  count: number;
  /** Of `count`, answered 401/403 (by scope or by the caller's mode). */
  rejected: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

export interface AuthUsageService {
  service: string;
  label: string;
  /** AUTH environment of the service: caller keys are resolved there. */
  target: string | null;
  /** Nothing could be read. */
  error: string | null;
  /** Read partly (some pods did not answer). */
  warning?: string | null;
  pods?: { total: number; answered: number } | null;
  callers: AuthUsageCaller[];
}

/** What a caller key names in AUTH. */
export interface CallerName {
  customerId: string;
  customer: string;
  tenantId?: string | null;
  tenant?: string | null;
}

const KEY = /^([a-z][a-z0-9-]{0,31}):(.+)$/;

/** `pms:1546` → `['pms', '1546']`; the value may itself hold `:` (gke `cluster:namespace`). */
export const splitKey = (callerKey: string): [string, string] | null => {
  const m = KEY.exec(callerKey.trim());
  return m ? [m[1], m[2]] : null;
};

/** One customer (or one unresolved key): its legacy and jwt rows together. */
export interface CallerGroup {
  key: string;
  name: CallerName | null;
  callers: AuthUsageCaller[];
  legacy: number;
  jwt: number;
  rejected: number;
  lastSeen: string | null;
  state: 'legacy' | 'migrating' | 'auth';
}

const isLegacy = (c: AuthUsageCaller) => c.method !== 'jwt';
const later = (a: string | null, b: string | null) => (!a ? b : !b ? a : a > b ? a : b);

/**
 * Rows grouped by the customer their key resolves to (`pms:1546` legacy and `tenant:<tid>` jwt of the same customer
 * are one line); unresolved keys stay on their own. Groups with legacy traffic first, the busiest first.
 */
export function groupCallers(
  s: AuthUsageService,
  nameOf: (s: AuthUsageService, c: AuthUsageCaller) => CallerName | undefined
): CallerGroup[] {
  const groups = new Map<string, CallerGroup>();
  for (const c of s.callers) {
    const name = nameOf(s, c) ?? null;
    const key = name ? `customer:${name.customerId}` : `key:${c.callerKey}`;
    let g = groups.get(key);
    if (!g) {
      g = { key, name, callers: [], legacy: 0, jwt: 0, rejected: 0, lastSeen: null, state: 'auth' };
      groups.set(key, g);
    }
    g.callers.push(c);
    if (isLegacy(c)) g.legacy += c.count;
    else g.jwt += c.count;
    g.rejected += c.rejected;
    g.lastSeen = later(g.lastSeen, c.lastSeen);
  }
  for (const g of groups.values()) {
    const hasLegacy = g.callers.some(isLegacy);
    const hasJwt = g.callers.some((c) => !isLegacy(c));
    g.state = hasLegacy ? (hasJwt ? 'migrating' : 'legacy') : 'auth';
    g.callers.sort((a, b) => Number(isLegacy(b)) - Number(isLegacy(a)) || b.count - a.count);
  }
  const rank = { legacy: 0, migrating: 1, auth: 2 };
  return [...groups.values()].sort(
    (a, b) =>
      rank[a.state] - rank[b.state] || b.legacy + b.jwt - (a.legacy + a.jwt) || a.key.localeCompare(b.key)
  );
}

/** A source that cannot be read, or only partly, is a warning; legacy callers are expected during the migration. */
export function authUsageIssues(list: AuthUsageService[]): Issue[] {
  return list
    .filter((s) => s.error || s.warning)
    .map((s) => ({
      key: `au:${s.service}:${s.target ?? ''}`,
      status: 'warn',
      name: s.label,
      what: 'auth usage',
      detail: s.error ? `cannot read auth usage: ${s.error}` : s.warning!,
      to: '/identity#migration',
    }));
}

const TTL = 60_000;
let cache: { at: number; data: Promise<AuthUsageService[]> } | null = null;
const names = new Map<string, Promise<CallerName | null>>();

async function fetchUsage(refresh: boolean): Promise<AuthUsageService[]> {
  const r = await hubFetch(`${BASE_URL}/auth-usage${refresh ? '?refresh=1' : ''}`);
  const body = await r.json().catch(() => null);
  if (!r.ok) throw new Error((body as { error?: string } | null)?.error || `HTTP ${r.status}`);
  return (body as AuthUsageService[]) ?? [];
}

/**
 * Best effort, in the service's environment (needs a session there): `tenant:<tid>` → that tenant, a legacy alias
 * key → the alias lookup; `client:` and `sub:` keys stay unnamed.
 */
function lookup(target: string | null, callerKey: string): Promise<CallerName | null> {
  const parts = splitKey(callerKey);
  if (!target || !parts || !targetSession(target).isSignedIn()) return Promise.resolve(null);
  const [system, value] = parts;
  if (system === 'client' || system === 'sub') return Promise.resolve(null);
  const key = `${target}|${system}|${value}`;
  let p = names.get(key);
  if (!p) {
    const auth = authClientFor(target);
    p = (
      system === 'tenant'
        ? auth.tenants.get(value).then((t) => ({
            customerId: t.customerId,
            customer: t.customerName ?? t.customerId,
            tenantId: t.id,
            tenant: t.name,
          }))
        : auth.lookupAlias(system, value).then((m) => ({
            customerId: m.customerId,
            customer: m.customerName,
            tenantId: m.tenantId,
            tenant: m.tenantName,
          }))
    ).catch(() => null);
    names.set(key, p);
  }
  return p;
}

/** `/auth-usage` (cached 60 s, as on the server) plus names for the caller keys. Needs ophub:auth:read. */
export function useAuthUsage() {
  const hub = useHubLogin();
  const enabled = hasScope(hub?.scopes, SCOPES.authRead);
  const [state, setState] = useState<{
    data: AuthUsageService[] | null;
    names: Record<string, CallerName>;
    loading: boolean;
    error: string | null;
  }>({ data: null, names: {}, loading: enabled, error: null });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    if (round > 0 || !cache || Date.now() - cache.at > TTL)
      cache = { at: Date.now(), data: fetchUsage(round > 0) };
    cache.data.then(
      async (data) => {
        if (!alive) return;
        setState((s) => ({ ...s, data, loading: false, error: null }));
        const found: Record<string, CallerName> = {};
        await Promise.all(
          data.flatMap((s) =>
            s.callers.map(async (c) => {
              const n = await lookup(s.target, c.callerKey);
              if (n) found[`${s.target ?? ''}|${c.callerKey}`] = n;
            })
          )
        );
        if (alive && Object.keys(found).length) setState((s) => ({ ...s, names: found }));
      },
      (e) => alive && setState((s) => ({ ...s, data: null, loading: false, error: (e as Error).message }))
    );
    return () => {
      alive = false;
    };
  }, [enabled, round]);
  return {
    ...state,
    enabled,
    nameOf: (s: AuthUsageService, c: AuthUsageCaller): CallerName | undefined =>
      state.names[`${s.target ?? ''}|${c.callerKey}`],
    reload: () => setRound((r) => r + 1),
  };
}

/** Test hook. */
export const resetAuthUsage = () => {
  cache = null;
  names.clear();
};
